// ============================================================================
// StudentAcademyView.jsx — Passo 45
// Painel do Aluno: Player + Playlist + Progresso + Teaser & Lock (Art. 103.4 + 106.2)
// MC1: split-screen + player + published filter
// MC2: progresso Firestore + CheckCircle2 + barra global
// MC3: isPro toggle + Lock overlay (Art. 103.4)
// ============================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { auth } from '../../shared/api/firebase/config';
import { subscribeAcademyProgress, markLessonComplete } from '../../services/StudentAcademyRepository';
import {
  CheckCircle2, ChevronDown, ChevronRight, Clock, FileText, GraduationCap,
  Lock, PlayCircle, Star, Video, BookOpen, CheckCheck, ArrowRight, Zap,
} from 'lucide-react';

// ── Dados de conteúdo publicado (espelha o AcademyStudio mock — filtragem por published) ─
const CATALOG = [
  {
    id: 'mod-1',
    title: 'Ética e Deontologia da OAB',
    description: 'Fundamentos do exercício ético da advocacia e o Estatuto da OAB.',
    lessons: [
      { id: 'les-1-1', title: 'Introdução ao Estatuto da Advocacia',     type: 'VIDEO', duration: 18, premium: false, videoUrl: '' },
      { id: 'les-1-2', title: 'Sigilo Profissional e seus Limites',       type: 'VIDEO', duration: 22, premium: false, videoUrl: '' },
      { id: 'les-1-3', title: 'Conflito de Interesses e Incompatibilidades', type: 'TEXT', duration: 12, premium: true, videoUrl: '' },
    ],
  },
  {
    id: 'mod-2',
    title: 'Processo Civil (CPC 2015)',
    description: 'Estudo sistemático do CPC e suas aplicações práticas.',
    lessons: [
      { id: 'les-2-1', title: 'Normas Fundamentais do CPC',           type: 'VIDEO', duration: 25, premium: false, videoUrl: '' },
      { id: 'les-2-2', title: 'Competência Absoluta e Relativa',      type: 'TEXT',  duration: 20, premium: false, videoUrl: '' },
      { id: 'les-2-3', title: 'Petição Inicial: Requisitos Formais',  type: 'VIDEO', duration: 30, premium: true,  videoUrl: '' },
    ],
  },
  {
    id: 'mod-3',
    title: 'Direito do Trabalho e CLT',
    description: 'Reforma Trabalhista, contratos atípicos e contencioso do trabalho.',
    lessons: [
      { id: 'les-3-1', title: 'Contrato de Trabalho: Modalidades',    type: 'VIDEO', duration: 28, premium: false, videoUrl: '' },
      { id: 'les-3-2', title: 'FGTS, Férias e 13º Salário',           type: 'TEXT',  duration: 15, premium: false, videoUrl: '' },
      { id: 'les-3-3', title: 'Reclamação Trabalhista no JUS Virtual', type: 'VIDEO', duration: 40, premium: true,  videoUrl: '' },
    ],
  },
];

// Todos os lessons publicados (flat list para o progresso global)
const ALL_LESSONS = CATALOG.flatMap((m) => m.lessons);
const TOTAL_LESSONS = ALL_LESSONS.length;

// ── Tipo de ícone ─────────────────────────────────────────────────────────────
const TYPE_META = {
  VIDEO: { icon: Video,    color: 'text-blue-500'    },
  TEXT:  { icon: FileText, color: 'text-emerald-500' },
  QUIZ:  { icon: CheckCheck, color: 'text-amber-500' },
};

// ── Player: iframe ou placeholder ─────────────────────────────────────────────
function VideoPlayer({ lesson, isPro, onMarkComplete, isCompleted, onUpgrade }) {
  const isLocked = lesson.premium && !isPro;

  if (isLocked) {
    return (
      <div className="w-full aspect-video bg-[#0f1120] rounded-2xl flex items-center justify-center relative overflow-hidden">
        {/* Fundo sfumato */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#20243B]/90 to-black/80" />
        {/* Overlay Premium — Art. 103.4 Teaser & Lock */}
        <div className="relative z-10 flex flex-col items-center gap-4 text-center px-6">
          <div className="w-16 h-16 rounded-full bg-[#C5A059]/10 border-2 border-[#C5A059]/30 flex items-center justify-center">
            <Lock className="w-8 h-8 text-[#C5A059]" />
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-[#C5A059] mb-1">Conteúdo PRO</div>
            <div className="text-base font-extrabold text-white">Este conteúdo é exclusivo para alunos PRO</div>
            <div className="text-xs text-white/60 mt-1">Faça upgrade para acessar este módulo e muito mais.</div>
          </div>
          <button
            type="button"
            onClick={onUpgrade}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#C5A059] text-[#20243B] text-xs font-extrabold uppercase tracking-widest hover:brightness-110 transition"
          >
            <Zap className="w-3.5 h-3.5" /> Fazer Upgrade PRO
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Player */}
      <div className="w-full aspect-video bg-[#0f1120] rounded-2xl overflow-hidden relative">
        {lesson.videoUrl ? (
          <iframe
            src={lesson.videoUrl}
            title={lesson.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center px-6">
            <PlayCircle className="w-16 h-16 text-white/10" />
            <div className="text-sm font-bold text-white/40">{lesson.title}</div>
            <div className="text-[10px] text-white/20 font-mono uppercase tracking-widest">
              {lesson.type === 'VIDEO' ? 'Player de Vídeo — URL não configurada' : 'Conteúdo Textual'}
            </div>
          </div>
        )}
      </div>

      {/* Info + botão de conclusão */}
      <div className="flex items-start justify-between gap-3 bg-white border border-gray-200 rounded-2xl px-5 py-4">
        <div className="min-w-0">
          <div className="text-xs font-extrabold text-[#20243B] uppercase tracking-widest flex items-center gap-2">
            {lesson.type}
            <span className="text-gray-300">·</span>
            <Clock className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 normal-case font-semibold">{lesson.duration}min</span>
          </div>
          <div className="text-lg font-black text-[#20243B] mt-1 leading-snug">{lesson.title}</div>
        </div>

        {/* MC2: Botão Marcar como Concluída */}
        <button
          type="button"
          onClick={onMarkComplete}
          disabled={isCompleted}
          className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-widest transition-all ${
            isCompleted
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
              : 'bg-[#20243B] text-[#C5A059] hover:bg-[#2d3352]'
          }`}
        >
          <CheckCircle2 className="w-4 h-4" />
          {isCompleted ? 'Concluída' : 'Marcar como Concluída'}
        </button>
      </div>
    </div>
  );
}

// ── Playlist Sidebar ──────────────────────────────────────────────────────────
function PlaylistSidebar({ modules, activeLesson, progress, isPro, onSelectLesson, completedCount }) {
  const [expanded, setExpanded] = useState(() => {
    const m = {};
    modules.forEach((mod) => { m[mod.id] = true; });
    return m;
  });

  const pct = TOTAL_LESSONS ? Math.round(completedCount / TOTAL_LESSONS * 100) : 0;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white border border-gray-200 rounded-2xl">
      {/* Cabeçalho progresso global — MC2 */}
      <div className="bg-[#20243B] px-4 py-3 flex-shrink-0">
        <div className="text-[10px] font-black uppercase tracking-widest text-[#C5A059] mb-1.5">Seu Progresso</div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-sm font-black text-white">{completedCount} de {TOTAL_LESSONS} aulas</span>
          <span className="text-xs font-bold text-[#C5A059]">{pct}%</span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#C5A059] rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Lista de módulos */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {modules.map((module) => (
          <div key={module.id}>
            {/* Cabeçalho do módulo */}
            <button
              type="button"
              className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 text-left"
              onClick={() => setExpanded((p) => ({ ...p, [module.id]: !p[module.id] }))}
            >
              <BookOpen className="w-3.5 h-3.5 text-[#C5A059] flex-shrink-0" />
              <span className="text-[11px] font-extrabold text-[#20243B] flex-1 truncate">{module.title}</span>
              {expanded[module.id]
                ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
            </button>

            {/* Aulas do módulo */}
            {expanded[module.id] && (
              <div className="pb-1">
                {module.lessons.map((lesson) => {
                  const isActive    = activeLesson?.id === lesson.id;
                  const isDone      = !!progress[lesson.id];
                  const isLocked    = lesson.premium && !isPro;
                  const TypeIcon    = TYPE_META[lesson.type]?.icon || FileText;
                  const typeColor   = TYPE_META[lesson.type]?.color || 'text-gray-400';

                  return (
                    <button
                      key={lesson.id}
                      type="button"
                      onClick={() => onSelectLesson(lesson)}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors ${
                        isActive
                          ? 'bg-[#C5A059]/10 border-l-2 border-[#C5A059]'
                          : 'hover:bg-gray-50 border-l-2 border-transparent'
                      }`}
                    >
                      {/* Status: done / active / locked / default */}
                      {isDone ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      ) : isLocked ? (
                        <Lock className="w-3.5 h-3.5 text-[#C5A059] flex-shrink-0" />
                      ) : (
                        <TypeIcon className={`w-3.5 h-3.5 flex-shrink-0 ${isActive ? 'text-[#C5A059]' : typeColor}`} />
                      )}

                      <div className="flex-1 min-w-0">
                        <div className={`text-[11px] font-medium truncate ${
                          isActive ? 'text-[#20243B] font-bold' : isDone ? 'text-gray-500 line-through' : 'text-gray-700'
                        }`}>
                          {lesson.title}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[9px] text-gray-400">{lesson.duration}min</span>
                          {lesson.premium && (
                            <span className="text-[8px] font-black uppercase tracking-widest text-[#C5A059] bg-[#C5A059]/10 px-1 py-0.5 rounded">PRO</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export function StudentAcademyView({ user: propUser }) {
  const user = propUser || auth?.currentUser;
  const uid  = user?.uid || null;

  // MC3: isPro toggle (Art. 103.4 — mockado, será integrado com Firestore em fase futura)
  const [isPro, setIsPro] = useState(false);

  // Módulo + aula selecionados
  const [selectedLesson, setSelectedLesson] = useState(CATALOG[0]?.lessons[0] || null);

  // MC2: Progresso — { [lessonId]: true }
  const [progress, setProgress] = useState({});
  const [savingProgress, setSavingProgress] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // MC2: Leitura em tempo real do progresso no Firestore
  useEffect(() => {
    if (!uid) return;
    const unsub = subscribeAcademyProgress(uid, (map) => {
      setProgress(map);
    }, () => {});
    return () => unsub();
  }, [uid]);

  // MC2: Marcar aula como concluída → Firestore
  const handleMarkComplete = useCallback(async () => {
    if (!selectedLesson || !uid) return;
    setSavingProgress(true);
    try {
      await markLessonComplete(uid, selectedLesson.id, selectedLesson.title);
      // Avançar para próxima aula
      const flat = CATALOG.flatMap((m) => m.lessons);
      const idx = flat.findIndex((l) => l.id === selectedLesson.id);
      if (idx !== -1 && idx < flat.length - 1) {
        setSelectedLesson(flat[idx + 1]);
      }
    } catch (e) {
      console.error('[RDA] academy progress save error:', e);
    } finally {
      setSavingProgress(false);
    }
  }, [selectedLesson, uid]);

  const completedCount = useMemo(() => Object.values(progress).filter(Boolean).length, [progress]);
  const isCurrentCompleted = selectedLesson ? !!progress[selectedLesson.id] : false;

  return (
    <div className="flex flex-col h-full gap-0">

      {/* ── Barra de topo ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-sm font-extrabold text-[#20243B] uppercase tracking-widest flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-[#C5A059]" /> Academia RDA — Cursos Jurídicos
          </h2>
          <p className="text-[10px] text-gray-400 mt-0.5">Art. 103.4 + 106.2 · Teaser & Lock · Progresso salvo no Firestore</p>
        </div>

        {/* MC3: Toggle isPro — demonstração comercial */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-500 font-bold">Modo de acesso:</span>
          <button
            type="button"
            onClick={() => setIsPro((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-extrabold border transition-all ${
              isPro
                ? 'bg-[#C5A059] text-[#20243B] border-[#C5A059]'
                : 'bg-white text-gray-500 border-gray-300 hover:border-[#C5A059]'
            }`}
          >
            <Star className="w-3.5 h-3.5" />
            {isPro ? 'PRO (simulado)' : 'FREE'}
          </button>
        </div>
      </div>

      {/* ── Layout split-screen (70/30) ── */}
      <div className="flex gap-4 flex-1 min-h-0">

        {/* 70% — Área do Player */}
        <div className="flex-1 min-w-0 flex flex-col gap-3">
          {selectedLesson ? (
            <VideoPlayer
              lesson={selectedLesson}
              isPro={isPro}
              isCompleted={isCurrentCompleted}
              onMarkComplete={handleMarkComplete}
              onUpgrade={() => setShowUpgradeModal(true)}
            />
          ) : (
            <div className="w-full aspect-video bg-gray-100 rounded-2xl flex items-center justify-center">
              <div className="text-center text-gray-400">
                <PlayCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <div className="text-sm font-bold">Selecione uma aula</div>
              </div>
            </div>
          )}

          {/* Navegação rápida entre aulas */}
          {selectedLesson && (
            <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-2.5">
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Próxima aula</span>
              {(() => {
                const flat = CATALOG.flatMap((m) => m.lessons);
                const idx  = flat.findIndex((l) => l.id === selectedLesson.id);
                const next = flat[idx + 1];
                if (!next) return <span className="text-[10px] text-emerald-600 font-bold">Curso concluído! 🎉</span>;
                return (
                  <button
                    type="button"
                    onClick={() => setSelectedLesson(next)}
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#20243B] hover:text-[#C5A059] transition"
                  >
                    {next.title} <ArrowRight className="w-3 h-3" />
                  </button>
                );
              })()}
            </div>
          )}
        </div>

        {/* 30% — Playlist */}
        <div className="w-72 flex-shrink-0">
          <PlaylistSidebar
            modules={CATALOG}
            activeLesson={selectedLesson}
            progress={progress}
            isPro={isPro}
            onSelectLesson={setSelectedLesson}
            completedCount={completedCount}
          />
        </div>
      </div>

      {/* ── Modal Upgrade PRO — Art. 103.4 ── */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[600] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl border border-[#C5A059]/30 w-full max-w-sm mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#20243B] px-6 py-5 text-center">
              <Star className="w-10 h-10 text-[#C5A059] mx-auto mb-2" />
              <div className="text-lg font-black text-white">Academia RDA PRO</div>
              <div className="text-xs text-white/60 mt-1">Acesso ilimitado a todos os módulos</div>
            </div>
            <div className="px-6 py-5 space-y-3">
              {['Todos os módulos desbloqueados', 'Certificado de conclusão em PDF', 'Atualizações vitalícias', 'Suporte prioritário do escritório'].map((f) => (
                <div key={f} className="flex items-center gap-2 text-xs text-gray-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  {f}
                </div>
              ))}
            </div>
            <div className="px-6 pb-6 space-y-2">
              <button
                type="button"
                onClick={() => { setIsPro(true); setShowUpgradeModal(false); }}
                className="w-full py-3 rounded-xl bg-[#C5A059] text-[#20243B] text-sm font-extrabold uppercase tracking-widest hover:brightness-110 transition"
              >
                Ativar PRO (Simulação)
              </button>
              <button
                type="button"
                onClick={() => setShowUpgradeModal(false)}
                className="w-full py-2 rounded-xl border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 transition"
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentAcademyView;
